import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

// Only the fields the server needs to price a line.
export function quoteItems(items) {
  return (items || []).map((item) => ({
    product_id: item.product_id ?? item.id,
    variant_id: item.variant_id ?? null,
    product_price_id: item.product_price_id ?? null,
    qty: Number(item.qty) || 1,
  }));
}

/**
 * Asks the server what the order costs: line prices, shipping, coupon, total.
 * Placing the order uses the same server rules, so the shown total is the
 * charged total. Use this once, e.g. to check a coupon before applying it.
 */
export function fetchCheckoutQuote({ items, city = "", shippingMethod = "standard", couponCode = null }) {
  return axios
    .post("/api/checkout/quote", {
      cart_items: quoteItems(items),
      city,
      shipping_method: shippingMethod,
      coupon_code: couponCode,
    })
    .then((res) => res.data)
    .catch((error) => {
      if (error.response?.data?.message) return error.response.data;
      throw error;
    });
}

/**
 * Live checkout totals for any store design. Re-asks the server when the
 * cart, city, shipping method, or coupon changes, after a short pause.
 * Returns { quote, loading, error }. While a new quote loads, the last one
 * stays on screen.
 */
export default function useCheckoutQuote({ items, city, shippingMethod, couponCode }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(0);

  const cartKey = useMemo(() => JSON.stringify(quoteItems(items)), [items]);

  useEffect(() => {
    const parsed = JSON.parse(cartKey);
    if (!parsed.length) {
      setQuote(null);
      return undefined;
    }
    const id = request.current + 1;
    request.current = id;
    setLoading(true);
    const timer = setTimeout(() => {
      fetchCheckoutQuote({ items: parsed, city: (city || "").trim(), shippingMethod, couponCode })
        .then((data) => {
          if (request.current !== id) return;
          if (data.status === 200) {
            setQuote(data);
            setError("");
          } else {
            setError(data.message || "The order total could not be worked out.");
          }
        })
        .catch(() => {
          if (request.current === id) setError("The order total could not be worked out. Check your connection.");
        })
        .finally(() => {
          if (request.current === id) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [cartKey, city, shippingMethod, couponCode]);

  return { quote, loading, error };
}
